import React, { useState } from "react";
import { useMediaQuery, useTheme } from "@mui/material";
import { Route, BrowserRouter as Router, Routes } from "react-router-dom";
import Gallery from "./components/Landing/Gallery/Gallery";
import OuterWrapper from "./components/Landing/OuterWrapper";
import { FaWhatsapp } from "react-icons/fa";
import { motion, useReducedMotion } from "framer-motion";
import ServiceCard from "./components/Landing/Services/ServiceCard";
import { services } from "./components/Landing/constant";
import ServiceInquiryModal from "./components/Landing/Modal/ServiceInquiryModal";
import { AboutUs } from "./components/Landing/AboutUs/AboutUs";
import OurProcess from "./components/Landing/OurProcess/OurProcess";
import ContactUs from "./components/Landing/ContactUs/ContactUs";
import PrivacyPolicy from "./components/Landing/Terms&Conditions/Terms&Conditions";
import Seo from "./components/Seo/Seo";
import KitchenAccessoriesPage from "./components/KitchenAccessories/KitchenAccessoriesPage";

const SHOW_GALLERY = false;

const App: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState("");
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const prefersReducedMotion = useReducedMotion();
  const siteImage =
    "https://www.vistafusionstudio.com/public-images/vista-images/Images/vistalogo.png";

  const handleWhatsAppClick = () => {
    window.open("https://wa.me/+919030356464", "_blank");
  };

  return (
    <>
      <Router>
        <Routes>
          <Route
            path="/"
            element={
              <>
                <Seo
                  title="Interior Design Studio in Hyderabad"
                  description="Vista Fusion Studio creates thoughtful interiors for homes and offices in Hyderabad. Modular solutions, renovations, and end-to-end design tailored to your space."
                  image={siteImage}
                  jsonLd={{
                    "@context": "https://schema.org",
                    "@type": "ProfessionalService",
                    name: "Vista Fusion Studio",
                    url: "https://www.vistafusionstudio.com/",
                    image: siteImage,
                    logo: siteImage,
                    description:
                      "Interior design studio in Hyderabad offering home, office, renovation, and modular solutions.",
                    areaServed: "Hyderabad, Telangana, India",
                  }}
                />
                <OuterWrapper />
              </>
            }
          />
          <Route
            path="/services"
            element={
              <>
                <Seo
                  title="Services"
                  description="Explore interior design services including home and office design, renovations, and modular solutions."
                  image={siteImage}
                />
                <ServiceCard
                  services={services}
                  setIsModalOpen={setIsModalOpen}
                  setSelectedService={setSelectedService}
                />
              </>
            }
          />
          <Route
            path="/space-saving-modular-kitchen-accessories"
            element={
              <>
                <Seo
                  title="10 Space-Saving Modular Kitchen Accessories"
                  description="Discover 10 smart modular kitchen accessories that maximise storage, reduce clutter and make everyday cooking easier. Explore practical kitchen storage ideas with Vista Fusion Studio."
                  type="article"
                  image={siteImage}
                  jsonLd={{
                    "@context": "https://schema.org",
                    "@type": "FAQPage",
                    mainEntity: [
                      {
                        "@type": "Question",
                        name: "What are the most useful modular kitchen accessories?",
                        acceptedAnswer: {
                          "@type": "Answer",
                          text: "Useful options include drawer organisers, spice and bottle pull-outs, pantry units, corner storage, under-sink organisers and deep drawers. The right combination depends on your layout and storage needs.",
                        },
                      },
                      {
                        "@type": "Question",
                        name: "Which kitchen accessories work well in a small kitchen?",
                        acceptedAnswer: {
                          "@type": "Answer",
                          text: "Slim pull-outs, drawer organisers and vertical pantry storage can make better use of limited cabinet space. Choose accessories to suit the actual dimensions of your kitchen.",
                        },
                      },
                      {
                        "@type": "Question",
                        name: "Which accessories are useful for Indian cooking?",
                        acceptedAnswer: {
                          "@type": "Answer",
                          text: "Spice storage, bottle pull-outs, deep drawers for cookware, corner storage and under-sink organisers are practical choices for many Indian kitchens. The best selection depends on cooking habits and cookware.",
                        },
                      },
                      {
                        "@type": "Question",
                        name: "Do I need to install every modular kitchen accessory?",
                        acceptedAnswer: {
                          "@type": "Answer",
                          text: "No. Select accessories based on how you use your kitchen. The goal is to solve genuine organisation or accessibility problems, not to fill every cabinet with mechanisms.",
                        },
                      },
                      {
                        "@type": "Question",
                        name: "How should I choose accessories for my kitchen?",
                        acceptedAnswer: {
                          "@type": "Answer",
                          text: "Consider your layout, cabinet dimensions, cooking habits, cookware sizes and the items you use most often. A kitchen designer can help match storage solutions to the available space.",
                        },
                      },
                    ],
                  }}
                />
                <KitchenAccessoriesPage />
              </>
            }
          />
          <Route
            path="/about-us"
            element={
              <>
                <Seo
                  title="About Us"
                  description="Learn about Vista Fusion Studio and our approach to functional, elegant interior design."
                  image={siteImage}
                />
                <AboutUs setIsModalOpen={setIsModalOpen} />
              </>
            }
          />
          <Route
            path="/our-process"
            element={
              <>
                <Seo
                  title="Our Process"
                  description="From consultation to execution, see how Vista Fusion Studio delivers end-to-end interior design."
                  image={siteImage}
                />
                <OurProcess />
              </>
            }
          />
          {SHOW_GALLERY && (
            <Route
              path="/gallery"
              element={
                <>
                  <Seo
                    title="Gallery"
                    description="View our interior design portfolio across residential and commercial spaces."
                    image={siteImage}
                  />
                  <Gallery />
                </>
              }
            />
          )}
          <Route
            path="/contact-us"
            element={
              <>
                <Seo
                  title="Contact Us"
                  description="Get in touch with Vista Fusion Studio to start your interior design project."
                  image={siteImage}
                />
                <ContactUs />
              </>
            }
          />
          <Route
            path="/privacy-policy"
            element={
              <>
                <Seo
                  title="Privacy Policy"
                  description="Read Vista Fusion Studio's privacy policy and data handling practices."
                  image={siteImage}
                  noIndex
                />
                <PrivacyPolicy />
              </>
            }
          />
        </Routes>

        <motion.div
          initial={
            prefersReducedMotion ? false : { opacity: 0, scale: 0.82, y: 18 }
          }
          animate={
            prefersReducedMotion
              ? undefined
              : {
                  opacity: 1,
                  scale: 1,
                  y: [0, -4, 0],
                }
          }
          transition={
            prefersReducedMotion
              ? undefined
              : {
                  opacity: { duration: 0.42 },
                  scale: { duration: 0.42 },
                  y: {
                    duration: 2.4,
                    repeat: Infinity,
                    ease: "easeInOut",
                  },
                }
          }
          style={{
            position: "fixed",
            bottom: isSmallScreen ? "88px" : "88px",
            right: "18px",
            zIndex: 1000,
            background:
              "linear-gradient(135deg, rgba(37,211,102,0.94), rgba(28,177,84,0.94))",
            borderRadius: "999px",
            padding: "14px 14px 12px 14px",
            boxShadow: "0 16px 32px rgba(16, 90, 42, 0.34)",
            cursor: "pointer",
            border: "1px solid rgba(255,255,255,0.58)",
            backdropFilter: "blur(2px)",
          }}
          onClick={handleWhatsAppClick}
          title="Chat on WhatsApp"
          aria-label="Chat with Vista Fusion Studio on WhatsApp"
        >
          <FaWhatsapp style={{ color: "white", fontSize: "30px" }} />
        </motion.div>
        <ServiceInquiryModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          selectedService={selectedService}
        />
      </Router>
    </>
  );
};

export default App;
